import React, {
  RefObject,
} from 'react';
import invert from 'lodash/invert';
import startCase from 'lodash/startCase';
import AddIcon from '@mui/icons-material/Add';
import Button from 'pages/Dashboard/components/Button';
import List from 'views/EMR/Settings/VisitNotes/List';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';
import NavigationTabs from 'pages/Dashboard/components/NavigationTabs';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  useTableReset,
} from 'core/hooks';
import SearchIcon from 'core/icons/search';
import SearchInput from 'pages/Dashboard/components/SearchInput';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useSyncSearchQuery,
} from 'pages/Dashboard/hooks/url';
import SETTINGS_PATHS from 'views/EMR/Settings/paths';

const configs = ['master-templates', 'section-templates', 'fields'] as const;
const configsByIndex = invert(configs);
const tabs = configs.map((key) => ({
  label: startCase(key),
  singularLabel: startCase(key).slice(0, key.length - 1),
  key,
}));
export type VisitNoteParams = { config: typeof configs[number] };

export default function VisitNoteSettings() {
  const { config = 'master-templates' } = useParams<VisitNoteParams>();
  const navigate = useNavigate();
  const { tableInstanceRef, reset } = useTableReset();
  const {
    searchQuery,
    setSearchQuery,
  } = useSyncSearchQuery();
  const activeIndex = Number(configsByIndex[config]);

  return (
    <div className="h-full w-full flex flex-col gap-4">
      <div className="flex px-4 w-full justify-between">
        <div className="flex gap-4">
          <NavigationTabs
            classes={{
              root: '!min-h-[unset]',
              tab: '!font-semibold !min-w-[unset] !w-fit',
              active: '!border-blue-500 !text-blue-500',
            }}
            tabs={tabs}
            activeIndex={activeIndex}
            onClick={(index) => {
              navigate(`${SETTINGS_PATHS.VISIT_NOTES}/${configs[index]}`);
              setSearchQuery('');
            }}
          />
          <SearchInput
            placeholder={`Enter ${tabs[activeIndex]?.singularLabel} Name...`}
            className="w-60 lg:w-96 h-6"
            value={searchQuery}
            onChange={(value) => {
              setSearchQuery(value);
              reset();
            }}
            inputProps={{ autoComplete: 'off' }}
            icon={<SearchIcon className="!w-4 !h-4 text-gray-400" />}
          />
        </div>
        <Button
          className="!h-8"
          variant="contained"
          color="primary"
          type="button"
          startIcon={<AddIcon className="!w-4 !h-4" />}
          onClick={() => {
            if (config === 'fields') {
              settingsEventChannel.emit('onTemplateFieldDialog', {});
            } else {
              settingsEventChannel.emit('onTemplateDetails', config);
            }
          }}
        >
          Add
          {' '}
          {tabs[activeIndex]?.singularLabel}
        </Button>
      </div>
      <div className=" relative flex flex-col flex-1 overflow-y-hidden">
        <div className="h-full w-full">
          <List
            tableRef={tableInstanceRef as RefObject<VirtualTableInstance>}
            params={{ searchQuery: searchQuery.trim() }}
            type={config}
          />
        </div>
      </div>
    </div>
  );
}
